/**
 * Formatting helpers for money, hours, and dates. Mono-friendly outputs
 * (tabular, no fancy spacing) so they line up in stat values and tables.
 */
import { padPcoNumber, type MeResponse } from '@coffman-tm/shared';

const USD_PRECISE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const USD_ROUNDED = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/**
 * Render an integer-cents value as USD currency.
 *
 * Default is **precise** (`$156,257.43`, `$38,040,804.00`) per ADR 0002 —
 * Joey wants exact cents shown everywhere. Pass `{ precise: false }` for
 * the rare case a whole-dollar rounded rendering is desired.
 */
export function formatMoneyCents(
  cents: number,
  opts: { precise?: boolean } = {},
): string {
  const precise = opts.precise !== false; // default true
  return (precise ? USD_PRECISE : USD_ROUNDED).format(cents / 100);
}

export function formatHours(hours: number): string {
  return hours.toFixed(1);
}

/**
 * Pad a stored PCO number to a 3-digit display form (`004` / `020`).
 * Thin alias over the shared `padPcoNumber` — the single source of truth,
 * also used by the API's xlsx cell + filename renderers — so on-screen and
 * on-file labels can never drift apart.
 */
export const formatPcoNumber = padPcoNumber;

/**
 * Joey's mental model for a billing package is "PCO {parent}.{n}" — n is
 * the submission number (the stable per-parent billing sequence). A
 * submission with no number yet falls back to the parent alone so the label
 * is still meaningful. Parent gets 3-digit padding; the submission number
 * renders as stored.
 */
export function formatSubmissionLabel(
  parentPcoNumber: string,
  submissionNumber: string | null,
): string {
  const parent = formatPcoNumber(parentPcoNumber);
  if (!submissionNumber) return `PCO ${parent}`;
  return `PCO ${parent}.${submissionNumber}`;
}

/**
 * Short submission label — padded parent + submission number with no "PCO"
 * prefix ("004.3"). Used inside the Start New dialog and the Approve & Send
 * close offer, where the surrounding copy already establishes the PCO context
 * and "Start 004.3" / "Closes 004.2 and starts 004.3" read cleaner than the
 * full "PCO 004.3" label.
 */
export function formatSubmissionShort(
  parentPcoNumber: string,
  submissionNumber: string,
): string {
  return `${formatPcoNumber(parentPcoNumber)}.${submissionNumber}`;
}

export function formatDateShort(iso: string): string {
  // 'YYYY-MM-DD' -> 'Sat 5/16'
  const d = parseYmd(iso);
  return d
    ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' })
    : iso;
}

export function formatDateMd(iso: string): string {
  // 'YYYY-MM-DD' -> 'M/D'
  const d = parseYmd(iso);
  return d ? d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : iso;
}

/**
 * Span from the Sunday starting the week that ends `firstWeekEnding` to the
 * Saturday `lastWeekEnding` — numeric M/D, no weekday: '6/21 – 7/4'. Weeks run
 * Sun–Sat, so the start is the first week-ending minus 6 days (mirrors the
 * server's weekStartFor).
 */
export function formatWeekRangeSpan(firstWeekEnding: string, lastWeekEnding: string): string {
  const first = parseYmd(firstWeekEnding);
  if (!first) return `${firstWeekEnding} – ${lastWeekEnding}`;
  const start = new Date(first.getFullYear(), first.getMonth(), first.getDate() - 6);
  const startMd = start.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  return `${startMd} – ${formatDateMd(lastWeekEnding)}`;
}

/** One billing week's full Sun–Sat range: '6/21 – 6/27'. */
export function formatWeekRange(weekEndingDate: string): string {
  return formatWeekRangeSpan(weekEndingDate, weekEndingDate);
}

/**
 * Month-name dates, for the PCO page's package rows and week table (2026-08-05).
 * Numeric M/D packs tightly but reads as arithmetic in a column of money;
 * 'Jul 11' is unambiguous at a glance. DISPLAY ONLY — deliberately new
 * functions rather than a change to the numeric ones above, which every other
 * surface still uses.
 */

/** 'YYYY-MM-DD' -> 'Jul 11'. */
export function formatMonthDay(iso: string): string {
  const d = parseYmd(iso);
  return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : iso;
}

/** 'YYYY-MM-DD' -> 'Sat, Jul 11' — a week-ending date, which is always a Saturday. */
export function formatWeekdayMonthDay(iso: string): string {
  const d = parseYmd(iso);
  return d
    ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : iso;
}

/** Sunday-through-Saturday span in month-name form: 'Jun 21 – Jul 4'. */
export function formatWeekRangeSpanLong(
  firstWeekEnding: string,
  lastWeekEnding: string,
): string {
  const first = parseYmd(firstWeekEnding);
  if (!first) return `${firstWeekEnding} – ${lastWeekEnding}`;
  const start = new Date(first.getFullYear(), first.getMonth(), first.getDate() - 6);
  const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${startLabel} – ${formatMonthDay(lastWeekEnding)}`;
}

const PACIFIC_DATETIME = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * Render a full ISO timestamp as Pacific wall-clock time, e.g.
 * '2026-05-24T22:15:00Z' -> '5/24, 3:15 PM PT'. Used where Joey needs the
 * actual clock time of an event (intake failures), not just the calendar
 * day — Coffman + Brandon both operate on Pacific time.
 */
export function formatDateTimePacific(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${PACIFIC_DATETIME.format(d)} PT`;
}

function parseYmd(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  // Construct at LOCAL midnight, not UTC. These dates are pure calendar days
  // ('YYYY-MM-DD', no time) and the formatters below render via
  // toLocaleDateString in local time — a UTC-midnight Date renders as the day
  // before in any negative-offset zone (2026-03-20 -> "3/19").
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * The project's GC display name (e.g. "Hoffman Construction"), for routing
 * user-facing copy that used to hardcode "Hoffman" — Coffman works multiple
 * GCs across projects, so copy should read from `project.gc_name` instead.
 * Falls back to "the GC" while auth hasn't loaded yet.
 */
export function gcName(me: MeResponse | null): string {
  return me?.project.gc_name ?? 'the GC';
}

/**
 * Short form of the GC's name for compact reference labels ("{gcShort} FWA",
 * "{gcShort} FWA #") — the first whitespace-token of gc_name, so "Hoffman
 * Construction" reads as "Hoffman" (matching today's field label and the
 * attached xlsx's "Hoffman FWA#" header exactly). Falls back to "GC". Full
 * sentences/prose should use `gcName` instead — this is for labels only.
 */
export function gcShort(me: MeResponse | null): string {
  const first = me?.project.gc_name?.trim().split(/\s+/)[0];
  return first || 'GC';
}

/**
 * The Saturday on or after a 'YYYY-MM-DD' calendar date — the week a report
 * bills into. Mirrors the server's `weekEndingDateFor()` exactly, UTC-based
 * for the same reason: a local-time boundary makes rows on the edge hop weeks
 * depending on the viewer's zone. Null when the input isn't a calendar date.
 */
export function weekEndingFor(reportDate: string | null): string | null {
  const m = reportDate ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(reportDate) : null;
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  date.setUTCDate(date.getUTCDate() + ((6 - date.getUTCDay() + 7) % 7));
  const y = date.getUTCFullYear();
  const mo = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/**
 * The Saturday on or after `now` (UTC). Returns 'YYYY-MM-DD'. Mirrors the
 * server-side weekEndingDateFor() so the frontend filters rollups to "this
 * week" using the same boundary.
 */
export function currentWeekEnding(now: Date = new Date()): string {
  const day = now.getUTCDay(); // 0=Sun .. 6=Sat
  const offset = (6 - day + 7) % 7;
  const target = new Date(now);
  target.setUTCDate(now.getUTCDate() + offset);
  const y = target.getUTCFullYear();
  const m = String(target.getUTCMonth() + 1).padStart(2, '0');
  const d = String(target.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
